import { Box, IconButton, Tooltip, Typography, styled } from '@mui/material';
import { useState } from 'react';

const StyledCheatSheetIcon = styled(Box)(({ theme }) => ({
  color: theme.palette.text.secondary,
  cursor: 'pointer',
  padding: '2px',
  borderRadius: '3px',
  fontSize: '16px',
  display: 'flex',
  alignItems: 'center',
  '&:hover': {
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.action.hover,
  },
}));

const StyledTooltipContent = styled(Box)(({ theme }) => ({
  maxWidth: '400px',
  padding: theme.spacing(1),
}));

const StyledTipSection = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(1),
  '&:last-child': {
    marginBottom: 0,
  },
}));

const StyledTipTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 'bold',
  fontSize: '12px',
  color: theme.palette.text.primary,
  marginBottom: theme.spacing(0.5),
}));

const StyledTipText = styled(Typography)(({ theme }) => ({
  fontSize: '11px',
  color: theme.palette.text.secondary,
  lineHeight: 1.4,
}));

const StyledExample = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(0.5, 1),
  marginTop: theme.spacing(0.5),
  fontFamily: 'monospace',
  fontSize: '10px',
}));

export const ChartSearchCheatSheet = ({ isShow }: { isShow: boolean }) => {
  const [open, setOpen] = useState(isShow);

  const handleToggle = () => {
    setOpen(!open);
  };

  const tooltipContent = (
    <StyledTooltipContent>
      <Box position="absolute" top={0} right={0}>
        <IconButton onClick={() => setOpen(false)}>
          <i className="ri-close-line" aria-hidden="true" />
        </IconButton>
      </Box>
      <StyledTipSection>
        <StyledTipTitle>Search Tips</StyledTipTitle>
        <StyledTipText>
          Search by contract name or description. While exact Flux contract names work best, these tips help when you're not sure of the exact name.
        </StyledTipText>
      </StyledTipSection>

      <StyledTipSection>
        <StyledTipTitle>1. Product Name + Description</StyledTipTitle>
        <StyledTipText>Combine product names with descriptive terms like product groups, spread, or contract types:</StyledTipText>
        <StyledExample>CRUDE DIFF 1st LINE BRENT VS DUBAI</StyledExample>
        <StyledExample>EBOB SPREAD</StyledExample>
      </StyledTipSection>

      <StyledTipSection>
        <StyledTipTitle>2. Contract Month/Year</StyledTipTitle>
        <StyledTipText>Add contract month (JAN, FEB, etc.) and/or year to narrow results:</StyledTipText>
        <StyledExample>CRUDE DIFF 1st LINE BRENT VS DUBAI JUL 27</StyledExample>
        <StyledExample>EBOB SPREAD JUN 27 JUL 27</StyledExample>
      </StyledTipSection>

      <StyledTipSection>
        <StyledTipTitle>3. Rolling Contracts</StyledTipTitle>
        <StyledTipText>For rolling contracts, use "rolling month" followed by the month number:</StyledTipText>
        <StyledExample>CRUDE DIFF 1st LINE BRENT VS DUBAI ROLLING MONTH 1</StyledExample>
        <StyledExample>EBOB SPREAD ROLLING MONTH 1</StyledExample>
      </StyledTipSection>

      <StyledTipSection>
        <StyledTipTitle>Need Help?</StyledTipTitle>
        <StyledTipText>
          View the complete list of Flux products with their exact names and symbols:{' '}
          <a href="https://app.onyxhub.co/reference/products/" target="_blank" rel="noopener noreferrer" style={{ color: 'white' }}>
            Product Reference
          </a>
        </StyledTipText>
      </StyledTipSection>
    </StyledTooltipContent>
  );

  return (
    <Tooltip
      title={tooltipContent}
      open={open}
      onClose={() => setOpen(false)}
      placement="bottom-start"
      arrow
      PopperProps={{
        sx: {
          '& .MuiTooltip-tooltip': {
            backgroundColor: 'background.paper',
            color: 'text.primary',
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: 3,
            maxWidth: 'none',
          },
        },
      }}
    >
      <StyledCheatSheetIcon onClick={handleToggle}>
        <i className="ri-information-2-fill" aria-hidden="true" />
      </StyledCheatSheetIcon>
    </Tooltip>
  );
};
